import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../../context/AppContext';
import { useSimulado } from '../../../context/SimuladoContext';
import { Link, useLocation } from 'react-router-dom';
import './QuestionGrid.css';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropDown';
import PlayCircleFilledWhiteOutlinedIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Plot from 'react-plotly.js';
import TextQuestion from '../TextQuestion/TextQuestion';
import SwotGrid from '../../statistics/statistics_content/SwotGrid';
import Recurso from '../Recurso/Recurso';

// Modo de teste - definir como true para testar o design do diagrama SWAT
const TEST_MODE = true; // Set to true to enable test mode
const TEST_COERENCIA = "AO ACASO"; // Can be "ESPERADO" or "AO ACASO"

// Função para processar LaTeX (copiada do TextQuestion)
const processLatex = (text) => {
    if (!text) return '';

    // Substitui <latex-bloco> por $$ para display math
    let processedText = text.replace(/<latex-bloco>/g, '$$');
    processedText = processedText.replace(/<\/latex-bloco>/g, '$$');

    // Substitui \\ por \\\\ para quebras de linha no LaTeX
    processedText = processedText.replace(/\\\\/g, '\\\\\\\\');

    // Substitui <negrito> e <italico> por tags HTML
    processedText = processedText.replace(/<negrito>/g, '<b>');
    processedText = processedText.replace(/<\/negrito>/g, '</b>');
    processedText = processedText.replace(/<italico>/g, '<i>');
    processedText = processedText.replace(/<\/italico>/g, '</i>');

    return processedText;
};

// Função para renderizar texto com LaTeX (copiada do TextQuestion)
const renderTextWithLatex = (text) => {
    const processedText = processLatex(text);

    // Se não há LaTeX, retorna o texto normal
    if (!processedText.includes('$$')) {
        return <div dangerouslySetInnerHTML={{ __html: processedText.replace(/\n/g, '<br/>') }} />;
    }

    // Divide o texto em partes (LaTeX e texto normal)
    const parts = processedText.split(/(\$\$.*?\$\$)/g);

    return parts.map((part, index) => {
        if (part.startsWith('$$') && part.endsWith('$$')) {
            // Remove os $$ e renderiza como LaTeX
            const latexContent = part.slice(2, -2);
            return (
                <div key={index} className="latex-equation" style={{
                    textAlign: 'center',
                    margin: '10px 0',
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #e9ecef'
                }}>
                    <span className="latex-content">{latexContent}</span>
                </div>
            );
        } else {
            // Texto normal
            return (
                <span key={index} dangerouslySetInnerHTML={{
                    __html: part.replace(/\n/g, '<br/>')
                }} />
            );
        }
    });
};

function QuestionGrid() {
    const { user } = useApp();
    const { simulado, provas } = useSimulado();
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [questionStatus, setQuestionStatus] = useState([]);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [sortOrder, setSortOrder] = useState('chronological');
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [activeStatistic, setActiveStatistic] = useState(null);

    const [dificuldade, setDificuldade] = useState(0)
    const [prioridade, setPrioridade] = useState('ALTA_TESTE');

    const [questionImage, setQuestionImage] = useState(null);
    const [questionTextContent, setQuestionTextContent] = useState({});
    const [questionStatistics, setQuestionStatistics] = useState(null);
    const [studentStatistics, setStudentStatistics] = useState(null);
    const [questionVideo, setQuestionVideo] = useState(null);
    const [questionVideoType, setQuestionVideoType] = useState(null);
    const [questionAnswer, setQuestionAnswer] = useState(null);
    const [questionSubjects, setQuestionSubjects] = useState({});
    const [questionAbbrevs, setQuestionAbbrevs] = useState({});
    const [questionProvas, setQuestionProvas] = useState({});
    const [availableQuestions, setAvailableQuestions] = useState({});
    const [hasVideo, setHasVideo] = useState({});
    const [hasTextVersion, setHasTextVersion] = useState({});
    const [userAnswers, setUserAnswers] = useState({});
    const [questionComentario, setQuestionComentario] = useState('');
    const [questionDetailedSolution, setQuestionDetailedSolution] = useState('');
    const [questionAnswers, setQuestionAnswers] = useState({});
    const [isRecursoOpen, setIsRecursoOpen] = useState(false);

    const [activeFilter, setActiveFilter] = useState('chronological');

    const [necessidadeEstudo, setNecessidadeEstudo] = useState({});
    const [questionDificuldade, setQuestionDificuldade] = useState({});
    const [chanceAcerto, setChanceAcerto] = useState({});
    const [coerencia, setCoerencia] = useState({});


    const [isDesempenhoDisponivel, setIsDesempenhoDisponivel] = useState(false);
    const [isEstatisticasDisponivel, setEstatisticasDisponivel] = useState(false);
    const [showResults, setShowResults] = useState(false);


    const location = useLocation();
    const expandedExam = location.state?.expandedExam || 'CH';

    const examSelectorRef = useRef(null);

    const [viewMode, setViewMode] = useState('imagem'); // 'imagem' ou 'texto'
    const [gabaritoViewMode, setGabaritoViewMode] = useState('imagem'); // 'imagem' ou 'texto'

    const [examDetailQuestion, setExamDetailQuestion] = useState(null);

    useEffect(() => {
        generateQuestions();
    }, []);

    useEffect(() => {
        if (expandedExam === 'RD') {
            fetchQuestionData(0);
        }
    }, [expandedExam]);

    useEffect(() => {

        const handleClickOutside = (event) => {
            if (examSelectorRef.current && !examSelectorRef.current.contains(event.target)) {
                setIsDropdownVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);



    useEffect(() => {

        const displayCoerencia = TEST_MODE ? TEST_COERENCIA : coerencia[selectedQuestion];
        const disponibilidade_desempenho = chanceAcerto[selectedQuestion] !== 0 && displayCoerencia !== 0;

        setIsDesempenhoDisponivel(disponibilidade_desempenho)

    }, [selectedQuestion, chanceAcerto, coerencia]);

    // Removido o useEffect que resetava o viewMode para 'imagem'
    // Agora o botão sempre aparece e quando não há texto mostra a mensagem

    useEffect(() => {

        // console.log(userAnswers)

        // if (dadosQuestao) {

        //     const disponibilidade_estatistica = (dadosQuestao.estatisticas && dadosQuestao.estatisticas.length > 0)
        //     setEstatisticasDisponivel(disponibilidade_estatistica)

        //   }

    }, [userAnswers])

    // Efeito para renderizar MathJax quando o gabarito mudar
    useEffect(() => {
        if (questionDetailedSolution && window.MathJax && gabaritoViewMode === 'texto') {
            // Aguarda um pouco para garantir que o DOM foi atualizado
            const timer = setTimeout(() => {
                if (window.MathJax.typesetPromise) {
                    window.MathJax.typesetPromise();
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [questionDetailedSolution, gabaritoViewMode]);




    const generateQuestions = async () => {
        const userID = user?.ID_usuario;
        const selectedSimulado = simulado?.id;

        if (!userID || !selectedSimulado) {
            console.error("Erro: userID ou selectedSimulado não encontrado.");
            return;
        }

        const status = {};
        const subjects = {};
        const abbrevsTemp = {};
        const provasTemp = {};
        const availableQuestionsTemp = {};
        const hasVideoTemp = {};
        const answersTemp = {};
        const necessidadesEstudoTemp = {};
        const chanceAcertoTemp = {};
        const coerenciaTemp = {};

        try {
            // 🔹 Busca desempenho do usuário
            const userPerformanceResponse = await fetch(
                `https://rvensino.com.br/get.php?action=getPerformance&ID_usuario=${userID}&ID_prova=${selectedSimulado}`
            );
            const performanceData = await userPerformanceResponse.json();

            if (!performanceData || performanceData.length === 0) {
                console.error("Nenhum dado de performance encontrado.");
                return;
            }

            const userAnswers = performanceData[0]?.Respostas || {};
            const necessidadeEstudoData = performanceData[0]?.necessidade_estudo || {};
            const chanceAcertoData = performanceData[0]?.chance_acerto || {};
            const coerenciaData = performanceData[0]?.coerencia || {};
            const simuladoStatus = performanceData[0]?.simulado_status ?? 0;
            const showResults = simuladoStatus >= 4;
            setShowResults(showResults);

            // 🔹 Busca dados das questões do simulado
            const infoSimuladoResponse = await fetch(
                `https://rvensino.com.br/get.php?action=get_info_simulado&ID_simulado=${selectedSimulado}`
            );
            const questionData = await infoSimuladoResponse.json();

            if (!questionData) {
                console.error("Erro ao buscar dados do simulado.");
                return;
            }

            for (const key of Object.keys(questionData)) {
                const i = parseInt(key);
                const questionInfo = questionData[key];
                if (questionInfo) {
                    const [subject, correctAnswer, videoAvailable, provaAbbr, abbrev] = questionInfo;

                    subjects[i] = subject;
                    abbrevsTemp[i] = abbrev;
                    provasTemp[i] = provaAbbr;
                    availableQuestionsTemp[i] = true;
                    hasVideoTemp[i] = videoAvailable;
                    answersTemp[i] = correctAnswer;

                    const userAnswer = userAnswers[i.toString()];

                    status[i] = (!showResults || typeof userAnswer === 'undefined') ? 'undefined' : (userAnswer === correctAnswer ? 'correct' : 'incorrect');

                    // Salva necessidade de estudo e chance de acerto
                    necessidadesEstudoTemp[i] = necessidadeEstudoData[i] ?? "";
                    chanceAcertoTemp[i] = chanceAcertoData[i] ?? 0;
                    coerenciaTemp[i] = coerenciaData[i] ?? "";
                }
            }

            setQuestionStatus(status);
            setQuestionSubjects(subjects);
            setQuestionAbbrevs(abbrevsTemp);
            setQuestionProvas(provasTemp);
            setAvailableQuestions(availableQuestionsTemp);
            setHasVideo(hasVideoTemp);
            setQuestionAnswers(answersTemp);
            setNecessidadeEstudo(necessidadesEstudoTemp);
            setChanceAcerto(chanceAcertoTemp);
            setCoerencia(coerenciaTemp);
            setUserAnswers(userAnswers);


        } catch (error) {
            console.error('Erro ao buscar performance do usuário ou dados de questão: ', error);
        }
    };






    const fetchQuestionData = async (questionNumber) => {
        try {
            const selectedSimulado = simulado?.id || '1';
            const response = await fetch(`https://rvensino.com.br/get.php?action=get_question&ID_simulado=${selectedSimulado}&questao=${questionNumber}`);
            const data = await response.json();
            console.log('question data')
            console.log(data)

            if (data) {
                setQuestionImage(data.link_imagem);
                setQuestionAnswer(data.gabarito);

                console.log(`link pro video: ${data.link_resolution_video}`)
                setQuestionVideo(data.link_resolution_video);
                setQuestionVideoType(data.video_type || 'vimeo'); // Default to 'vimeo' if not provided


                setQuestionComentario(data.comentario);
                setQuestionDetailedSolution(data.detailed_solution || '');

                // 🔹 Salva se a questão tem versão em texto
                setHasTextVersion(prev => ({
                    ...prev,
                    [questionNumber]: data.has_text_version || false
                }));

                // 🔹 Salva os dados de texto se disponíveis
                if (data.has_text_version && data.statement) {
                    setQuestionTextContent(prev => ({
                        ...prev,
                        [questionNumber]: {
                            statement: JSON.parse(data.statement),
                            command: data.command,
                            alternatives: {
                                A: data.alternative_a,
                                B: data.alternative_b,
                                C: data.alternative_c,
                                D: data.alternative_d,
                                E: data.alternative_e
                            }
                        }
                    }));
                } else {
                    setQuestionTextContent(prev => ({
                        ...prev,
                        [questionNumber]: null
                    }));
                }

                // 🔹 Obtém a dificuldade (usa 0 se for null)
                setQuestionDificuldade(prevDificuldades => ({
                    ...prevDificuldades,
                    [questionNumber]: data.dificuldade ?? 0
                }));

                // 🔹 Processa estatísticas de respostas
                if (data.statistics_percent) {
                    const parsedStats = JSON.parse(data.statistics_percent);
                    setQuestionStatistics({
                        x: Object.keys(parsedStats),
                        y: Object.values(parsedStats),
                        qty: JSON.parse(data.statistics_qty || "{}")
                    });
                } else {
                    setQuestionStatistics(null);
                }
            } else {
                console.error('Dados não encontrados para a questão:', questionNumber);
                setQuestionImage(null);
                setQuestionTextContent(null);
                setQuestionAnswer(null);
                setQuestionVideo(null);
                setQuestionComentario(null);
                setQuestionStatistics(null);
                setHasTextVersion(prev => ({ ...prev, [questionNumber]: false }));
                setQuestionDificuldade(prev => ({ ...prev, [questionNumber]: 0 }));
            }
        } catch (error) {
            console.error('Erro ao buscar dados da questão:', error);
             // Reset states on error
             setQuestionImage(null);
             setQuestionTextContent(null);
             setQuestionAnswer(null);
             setQuestionVideo(null);
             setQuestionComentario(null);
             setQuestionStatistics(null);
             setHasTextVersion(prev => ({ ...prev, [questionNumber]: false }));
             // Optionally reset difficulty or keep previous state
        }
    };






    const handleQuestionClick = (index) => {
        if (availableQuestions[index]) {
            setSelectedQuestion(index);
            setIsCollapsed(false);
            fetchQuestionData(index);
            fetchExamDetails(); // Chama a função ao clicar no square
        }
    };

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };


    const handleSortChange = (order) => {
        setSortOrder(order);
        setActiveFilter(order);
        setIsDropdownVisible(false);
    };


    const abbreviateSubject = (subject) => {
        if (subject === 'matematica') return 'Mat';
        if (subject === 'biologia') return 'Bio';
        if (subject === 'quimica') return 'Qui';
        if (subject === 'fisica') return 'Fis';
        return subject; // Return subject as is for all other subjects
    };

    const getSortedQuestions = () => {
        const questions = Object.keys(questionProvas)
            .map(Number)
            .filter(n => questionProvas[n] === expandedExam && n !== 0)
            .sort((a, b) => a - b);


        if (sortOrder === 'correct-first') {
            return questions.filter(q => questionStatus[q] === 'correct')
                            .concat(questions.filter(q => questionStatus[q] !== 'correct'));
        } else if (sortOrder === 'wrong-first') {
            return questions.filter(q => questionStatus[q] === 'incorrect')
                            .concat(questions.filter(q => questionStatus[q] !== 'incorrect'));
        }

        return questions;
    };


    const sortedQuestions = getSortedQuestions();

    const toggleDropdown = () => {
        setIsDropdownVisible(!isDropdownVisible);
    };

    const toggleStatistics = (type) => {
        setActiveStatistic((prevStatistic) => (prevStatistic === type ? null : type));
    };


    const handleNextQuestion = () => {
        const currentIndex = sortedQuestions.indexOf(selectedQuestion);
        if (currentIndex < sortedQuestions.length - 1) {
            const nextQuestion = sortedQuestions[currentIndex + 1];
            setSelectedQuestion(nextQuestion);
            fetchQuestionData(nextQuestion); // Update the question data
        }
    };


    const handlePreviousQuestion = () => {
        const currentIndex = sortedQuestions.indexOf(selectedQuestion);
        if (currentIndex > 0) {
            const prevQuestion = sortedQuestions[currentIndex - 1];
            setSelectedQuestion(prevQuestion);
            fetchQuestionData(prevQuestion);
        }
    };


    const fetchExamDetails = async () => {
        const userID = user?.ID_usuario || '1';
        const selectedSimulado = simulado?.id || '10';

        try {
            const response = await fetch(
                `https://rvensino.com.br/get.php?action=detalhes&user_id=${userID}&exam_id=${selectedSimulado}`
            );
            const data = await response.json();
            // Pega a primeira questão da lista
            const firstKey = Object.keys(data.questions)[0];
            setExamDetailQuestion(data.questions[firstKey]);
            console.log(`https://rvensino.com.br/get.php?action=detalhes&user_id=${userID}&exam_id=${selectedSimulado}`)
            console.log('Exam Details:', data);
        } catch (error) {
            console.error('Erro ao buscar detalhes do exame:', error);
            setExamDetailQuestion(null);
        }
    };

    // Call this function where needed, e.g., in useEffect or a button click
    // fetchExamDetails();


    return (
        <div className="questions-container">


            {/* testando!!! */}
            {/* <video controls key={questionVideo} autoPlay>
                <source src="https://player.vimeo.com/video/1044105952?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" type="video/mp4" />Seu browser não suporta a tag de video.
            </video> */}
            {/* <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}> */}
                {/* <iframe
                    src="https://player.vimeo.com/video/1044105868?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="questao_110">
                </iframe> */}
            {/* </div> */}
            {/* <iframe
                src="https://player.vimeo.com/video/1044105868?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479"
                frameBorder="0"
                allow="autoplay; clipboard-write"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title="questao_110">
            </iframe> */}




            <div className='question-area'>
                <div className='question-buttons'>
                    <div className='exam-selector' ref={examSelectorRef}>
                        {provas.map(({ prova, prova_abbr }) => (
                            <Link key={prova_abbr} to="/detalhes" state={{ expandedExam: prova_abbr }} className="no-link-style">
                                <div className='exam'>{prova_abbr}</div>
                            </Link>
                        ))}


                        {(expandedExam !== 'RD') && (
                            <div className='filter-icon' onClick={toggleDropdown}>
                                <FilterListOutlinedIcon style={{ color: '#242828', fontSize: 35 }} className='filter-icon' />
                                    {isDropdownVisible && (
                                        <div className="dropdown">
                                        <div
                                            className={`dropdown-item ${activeFilter === 'chronological' ? 'active-filter' : ''}`}
                                            onClick={() => handleSortChange('chronological')}
                                        >
                                            1,2,3,...
                                        </div>
                                        <div
                                            className={`dropdown-item ${activeFilter === 'correct-first' ? 'active-filter' : ''}`}
                                            onClick={() => handleSortChange('correct-first')}
                                        >
                                            Corretas
                                        </div>
                                        <div
                                            className={`dropdown-item ${activeFilter === 'wrong-first' ? 'active-filter' : ''}`}
                                            onClick={() => handleSortChange('wrong-first')}
                                        >
                                            Erradas
                                        </div>
                                    </div>

                                    )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid-container">
                    {expandedExam !== 'RD' ? (
                        <div className='question-container'>

                            <div className={`grid ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                            {sortedQuestions.map((questionNumber, index) => (
                                (!isCollapsed || selectedQuestion === questionNumber) && (
                                    <div
                                        key={questionNumber}
                                        className={`square ${questionAnswers[questionNumber] === 'X' ? 'anulada' : ''} ${availableQuestions[questionNumber] ? questionStatus[questionNumber] : 'unavailable'} ${selectedQuestion === questionNumber ? 'selected' : ''}`}
                                        onClick={() => handleQuestionClick(questionNumber)}
                                        style={{ cursor: availableQuestions[questionNumber] ? 'pointer' : 'default' }} // Make unclickable if not in availableQuestions
                                        >
                                        {questionNumber}
                                        <span className={`subject ${questionStatus[questionNumber]}`}>
                                            <span className="first-letter">{questionAbbrevs[questionNumber]?.replace(/^./, c => c.toUpperCase())}</span>
                                        </span>
                                        {hasVideo[questionNumber] && (
                                            <span className={`camera-icon ${questionStatus[questionNumber]}`}>
                                            <PlayCircleFilledWhiteOutlinedIcon style={{ fontSize: 17 }} />
                                            </span>
                                        )}
                                    </div>
                                )
                            ))}

                                {selectedQuestion !== null && (
                                    <div
                                        className="square toggle-square"
                                        onClick={toggleCollapse}
                                    >
                                        {isCollapsed ? <ArrowDropUpIcon style={{ color: '#242828', fontSize: 50 }} /> : <ArrowDropDownIcon style={{ color: '#242828', fontSize: 50 }} />}
                                    </div>
                                )}

                                {selectedQuestion !== null && (


                                    <button
                                        className="arrow-button left-arrow"
                                        onClick={handlePreviousQuestion}
                                        disabled={selectedQuestion === sortedQuestions[0]}
                                    >
                                        <ArrowBackIosIcon />
                                    </button>

                                )}

                                {selectedQuestion !== null && (

                                    <button
                                    className="arrow-button right-arrow"
                                    onClick={handleNextQuestion}
                                    disabled={selectedQuestion === sortedQuestions[sortedQuestions.length - 1]}
                                >
                                    <ArrowForwardIosIcon />
                            </button>

                                )}



                            </div>

                            {selectedQuestion !== null && (
                                <div className="question-box-container">
                                    <div className={`question-box ${isCollapsed ? 'collapsed' : ''}`}>


                                            <button
                                                className="toggle-view-btn"
                                                onClick={() => setViewMode(viewMode === 'imagem' ? 'texto' : 'imagem')}
                                                style={{ marginBottom: 12 }}
                                            >
                                                {viewMode === 'imagem' ? 'Ver em texto' : 'Ver em imagem'}
                                            </button>



                                        {viewMode === 'texto' ? (
                                            hasTextVersion[selectedQuestion] ? (
                                                <TextQuestion
                                                    questionNumber={selectedQuestion}
                                                    questionAnswer={questionAnswer}
                                                    userAnswer={userAnswers[selectedQuestion]}
                                                    textData={questionTextContent[selectedQuestion]}
                                                />
                                            ) : (
                                                <div style={{ textAlign: 'center', padding: '20px', fontSize: '16px', color: '#666' }}>
                                                    Formato Texto Indisponível para Questão
                                                </div>
                                            )
                                        ) : questionImage ? (
                                            <div className='question-img-container'>
                                                <img src={questionImage} className="question-img" alt={`Questão ${selectedQuestion}`} />
                                            </div>
                                        ) : (
                                            <div className="question-loading">Carregando questão...</div>
                                        )}
                                    </div>
                                </div>
                            )}



                        </div>
                    ) : null}
                </div>

                <div className='essay-corrected'>
                    {expandedExam === 'RD' && questionImage ? (
                        <div className="question-box-container">
                            <div className="question-box">
                                <div className='question-img-container'>
                                    <img src={questionImage} className="question-img" alt="Redação" />
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className='question-details-area'>
                {(selectedQuestion !== null) && expandedExam !== 'RD' && (
                    <div className="question-sidebar">
                        <div className="question-sidebar-statistic-container">



                            <div className="question-sidebar-header">
                                <p className='question-sidebar-title'>Detalhes da Questão {selectedQuestion}</p>
                                <div className="question-sidebar-info">

                                <div className="difficulty-container">
                                    <span className="difficulty-label">Dificuldade:</span>
                                        {/*<span
                                            key={index}
                                            className={`dot ${index < (examDetailQuestion?.dificuldde ?? questionDificuldade[selectedQuestion] ?? 0) ? 'filled' : ''}`}
                                        ></span>*/}
                                    <div className="difficulty-dots">
                                        {[...Array(5)].map((_, index) => (
                                            <span
                                                key={index}
                                                className={`dot ${index < (examDetailQuestion?.dificuldade ?? questionDificuldade[selectedQuestion] ?? 0) ? 'filled' : ''}`}
                                            ></span>
                                        ))}
                                    </div>
                                </div>

                                    <div className="gabarito-container">
                                        Gabarito: <span className="gabarito-circle">{questionAnswer}</span>
                                    </div>
                                </div>
                            </div>

                            {/* <p className='question-sidebar-title'>Detalhes da Questão {selectedQuestion}</p> */}

                            <div className="priority-container">
                                <span className="priority-label">Prioridade:</span>
                                {/*<span className={`priority-item ${(examDetailQuestion?.prioridade ?? necessidadeEstudo[selectedQuestion]) === 'regular' ? 'active' : ''}`}>*/}
                                    <span className={`priority-item ${(examDetailQuestion?.prioridde ?? necessidadeEstudo[selectedQuestion]) === 'regular' ? 'active' : ''}`}>
                                    REGULAR
                                </span>
                                {/*<span className={`priority-item ${(examDetailQuestion?.prioridade ?? necessidadeEstudo[selectedQuestion]) === 'alta' ? 'active' : ''}`}>*/}
                                    <span className={`priority-item ${(examDetailQuestion?.prioridde ?? necessidadeEstudo[selectedQuestion]) === 'alta' ? 'active' : ''}`}>
                                    ALTA
                                </span>
                                {/*<span className={`priority-item ${(examDetailQuestion?.prioridade ?? necessidadeEstudo[selectedQuestion]) === 'urgente' ? 'active' : ''}`}>*/}
                                <span className={`priority-item ${(examDetailQuestion?.prioridde ?? necessidadeEstudo[selectedQuestion]) === 'urgente' ? 'active' : ''}`}>
                                    URGENTE
                                </span>
                            </div>




                            <div className="question-buttons-container">


                                {/*
                             <div
                                className={`exam details ${activeStatistic === 'Resposta do Aluno' ? 'active' : ''} ${!isDesempenhoDisponivel ? 'unavailable' : ''}`}
                                onClick={() => isDesempenhoDisponivel && toggleStatistics('Resposta do Aluno')}
                            > 
                            */}

                            <div
                                className="exam details unavailable"
                            >
                                Desempenho do Aluno
                            </div>



                                {activeStatistic && (
                                    <div className="popup-overlay">
                                        <div className="popup-content">
                                            <div className='question-body'>

                                                {activeStatistic === 'Resposta do Aluno' && (
                                                <div className='question-details-answer'>

                                                    <p className='question-details-label'>&emsp; De acordo com a TRI, as suas chances de acerto da questão eram de:</p>
                                                    {chanceAcerto[selectedQuestion] ? `${chanceAcerto[selectedQuestion]}%` : "-"}
                                                    <p className='question-details-label'>&emsp; O que resulta em uma necessidade de estudo considerada:</p>
                                                    <div className="priority-container desempenho">
                                                    <span className="priority-label"></span>
                                                    <span className={`priority-item ${(examDetailQuestion?.prioridde ?? necessidadeEstudo[selectedQuestion]) === 'regular' ? 'active' : ''}`}>
                                                        REGULAR
                                                    </span>
                                                    <span className={`priority-item ${(examDetailQuestion?.prioridde ?? necessidadeEstudo[selectedQuestion]) === 'alta' ? 'active' : ''}`}>
                                                        ALTA
                                                    </span>
                                                    <span className={`priority-item ${(examDetailQuestion?.prioridde ?? necessidadeEstudo[selectedQuestion]) === 'urgente' ? 'active' : ''}`}>
                                                        URGENTE
                                                    </span>
                                                    </div>
                                                    <p className='question-details-label'>&emsp; Na análise de coerência, sua resposta se enquadra em:</p>


                                                    {/* <div className="swat-diagram"> ... antigo comentado */}
                                                    <div className='swot-grid-container'>
                                                        <SwotGrid />
                                                    </div>




                                                    {/* {userAnswers[selectedQuestion] || 'No answer available'} */}
                                                </div>
                                            )}

                                            </div>

                                        </div>
                                    </div>
                                )}

                                {/*<div className={`exam details ${activeStatistic === 'Estatísticas da Questão' ? 'active' : ''} ${!questionStatistics ? 'unavailable' : ''}`} onClick={() => questionStatistics && toggleStatistics('Estatísticas da Questão')}>Estatísticas da Questão</div>*/}
                             {/*<div className={`exam details ${activeStatistic === 'Estatísticas da Questão' ? 'active' : ''} ${!questionStatistics ? 'unavailable' : 'unavailable'}`} onClick={() => {}}>Estatísticas da Questão</div> */}

                              <div className="exam details unavailable">Estatísticas da Questão</div>

                                {activeStatistic && (
                                    <div className="popup-overlay">
                                        <div className="popup-content">
                                            <div className='question-body'>
                                                {/* {activeStatistic === 'Estatísticas da Questão' && <div className='question-details-image'><img src={questionStatistics}></img></div>} */}
                                                {activeStatistic === 'Estatísticas da Questão' && questionStatistics && (
                                                    <div className='question-details-chart'>
                                                        <Plot
                                                            data={[
                                                                {
                                                                    x: questionStatistics.x, // Alternativas ['A', 'B', 'C', 'D', 'E', 'Branco']
                                                                    y: questionStatistics.y, // Percentuais de resposta
                                                                    type: 'bar',
                                                                    marker: {
                                                                        color: questionStatistics.x.map(option => {
                                                                            if (option === questionAnswer) {
                                                                                return '#5faa5f'; // Verde para a alternativa correta
                                                                            } else if (option === userAnswers[selectedQuestion]) {
                                                                                return '#ff6767'; // Vermelho forte para a alternativa errada marcada pelo aluno
                                                                            } else {
                                                                                return '#ff676760'; // Vermelho transparente para as erradas não marcadas
                                                                            }
                                                                        }),
                                                                    },
                                                                    text: questionStatistics.x.map(option => {
                                                                        if (option === questionAnswer && option === userAnswers[selectedQuestion]) {
                                                                            return '✔️'
; // Check verde para resposta correta do aluno
                                                                        } else if (option === userAnswers[selectedQuestion] && option !== questionAnswer) {
                                                                            return '❌'; // Exclamação vermelha para resposta errada do aluno
                                                                        }
                                                                        return ''; // Sem ícone para outras barras
                                                                    }),
                                                                    textposition: 'outside', // Posiciona os ícones acima das barras
                                                                    hovertemplate:
                                                                        '<span style="font-size:18px">(%{y}%)</span><extra></extra>',
                                                                    customdata: questionStatistics ? Object.values(questionStatistics.qty) : [1]

                                                                },
                                                            ]}
                                                            layout={{
                                                                title: { text: '', font: { size: 20 } },
                                                                xaxis: { title: '', tickfont: { size: 16 } },
                                                                yaxis: { title: 'Distribuição de Respostas (%)', tickfont: { size: 16 } },
                                                                margin: { t: 30, l: 50, r: 30, b: 50 },
                                                                font: { family: 'Arial, sans-serif', size: 16 },
                                                                paper_bgcolor: 'rgba(0,0,0,0)',
                                                                plot_bgcolor: 'rgba(0,0,0,0)',
                                                            }}
                                                            config={{
                                                                responsive: true,
                                                                displayModeBar: false,
                                                            }}
                                                            style={{ width: '450px', height: '350px' }}
                                                        />
                                                    </div>
                                                )}







                                            </div>

                                        </div>
                                    </div>
                                )}

                                <div className={`exam details ${activeStatistic === 'Gabarito' ? 'active' : ''}`} onClick={() => toggleStatistics('Gabarito')}>Gabarito Comentado</div>
                                    {activeStatistic && (
                                        <div className="popup-overlay">
                                            <div className="popup-content">
                                                <div className='question-body'>

                                                {activeStatistic === 'Gabarito' && (
                                                    <div className='question-details-answer'>


                                                        <button
                                                            className="toggle-view-btn"
                                                            onClick={() => setGabaritoViewMode(gabaritoViewMode === 'imagem' ? 'texto' : 'imagem')}
                                                            style={{ marginBottom: 12 }}
                                                        >
                                                            {gabaritoViewMode === 'imagem' ? 'Ver em texto' : 'Ver em imagem'}
                                                        </button>

                                                        {gabaritoViewMode === 'texto' ? (
                                                            questionDetailedSolution ? (
                                                                <div className="question-text-content">
                                                                    <div className="question-text-body">
                                                                        {/* <div className="question-text-options">
                                                                            <div className="question-text-option gabarito">
                                                                                <span className="option-letter gabarito">{questionAnswer}</span>
                                                                                <span>{questionTextContent[selectedQuestion]?.alternatives?.[questionAnswer] || 'Alternativa não disponível'}</span>
                                                                            </div>
                                                                        </div> */}
                                                                        {renderTextWithLatex(questionDetailedSolution)}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div style={{ textAlign: 'center', padding: '20px', fontSize: '16px', color: '#666' }}>
                                                                    Gabarito em texto não disponível para esta questão
                                                                </div>
                                                            )
                                                        ) : (
                                                            questionComentario && (
                                                                <div className='question-img-container gabarito'>
                                                                    <img src={`https://rvensino.com.br/${questionComentario}`} className="question-img" />
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                )}

                                                </div>

                                            </div>
                                        </div>
                                    )}



                                <div
                                    className={`exam details ${activeStatistic === 'Resolução em Vídeo' ? 'active' : ''} ${!hasVideo[selectedQuestion] ? 'unavailable' : ''}`}
                                    onClick={() => hasVideo[selectedQuestion] && toggleStatistics('Resolução em Vídeo')}
                                >
                                    Resolução em Vídeo
                                </div>

                                {activeStatistic && (
                            <div className="popup-overlay">
                                <div className="popup-content">
                                    <div className='question-body'>
                                        {activeStatistic === 'Resolução em Vídeo' && (
                                            <div className='question-details-video' style={{ width: '400px', height: '220px', margin: '0' }}>
                                                {hasVideo[selectedQuestion] && (
                                                    <>
                                                        {questionVideoType === 'vimeo' ? (
                                                            // Render Vimeo iframe for vimeo videos
                                                            <iframe
                                                                src={`https://player.vimeo.com/video/${questionVideo}?h=be04853be1&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=5847`}
                                                                frameBorder="0"
                                                                allow="autoplay; fullscreen"
                                                                allowFullScreen
                                                                title={`questao_${selectedQuestion}`}
                                                                style={{ width: '100%', height: '100%' }}
                                                            >
                                                            </iframe>
                                                        ) : (
                                                            // Render direct video element for local videos
                                                            <video controls key={questionVideo} style={{ width: '100%', height: '100%' }}>
                                                                <source src={questionVideo} type="video/mp4" />
                                                                Seu browser não suporta a tag de video.
                                                            </video>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                                )}

                                <div className="exam details unavailable">
                                    Solicitar Recurso
                                </div>

                                {activeStatistic === 'Recurso' && (
                                    <Recurso questionNumber={selectedQuestion} />
                                )}






                            </div>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
}

export default QuestionGrid;
